// qs-useCanvasEvents.ts
import { TaperingEngine } from '@/brushes/engines/qs-brush-engine-tapering'
import { useDrawingOperations } from '@/composables/qs-useDrawingOperations'
import { useModeManager } from '@/composables/qs-useModeManager'
import { useCanvasStore } from '@/stores/qs-store-canvas'
import { useLayerStore } from '@/stores/qs-store-layers'
import { usePanelStore } from '@/stores/qs-store-panels'
import { useToolStore } from '@/stores/qs-store-tools'
import type { KonvaStage } from '@/types/qs-type-konva'
import type { TextObject } from '@/types/qs-types-index'
import { cursorManager } from '@/utils/qs-cursorManager'
import { useEventListener, useThrottleFn } from '@vueuse/core'
import Konva from 'konva'

export function useCanvasEvents() {
  const canvasStore = useCanvasStore()
  const layerStore = useLayerStore()
  const toolStore = useToolStore()
  const modeManager = useModeManager()
  const drawingOperations = useDrawingOperations()

  let panningAnimationFrame: number | null = null

  const { startDrawing, continueDrawing, endDrawing, getCanvasPosition } = drawingOperations
  const handleForceCanvasReinit = (): void => {
    window.dispatchEvent(new CustomEvent('force-canvas-reinit'))
  }

  const handleVisibilityChange = (): void => {
    if (document.hidden && canvasStore.isDrawing) {
      canvasStore.setDrawing(false)
      canvasStore.setCurrentPath(null)
      canvasStore.setBrushLines([])
    }
  }

  const handleLayerChange = (event: CustomEvent): void => {
    const { layers } = event.detail
    if (layers) {
      layerStore.layers = layers
    }
  }

  const handleLayerSelected = (event: CustomEvent): void => {
    const { layerId } = event.detail
    if (layerId) {
      const success = layerStore.setCurrentLayer(layerId)
      if (!success) {
        console.warn('Failed to select layer - may be locked:', layerId)
      }
    }
  }

  const handleToolStateRefresh = (event: CustomEvent): void => {
    const { toolState } = event.detail
    if (!toolState) return

    if (toolState.color) toolStore.setColor(toolState.color)
    if (toolState.brushSize) toolStore.setBrushSize(toolState.brushSize)
    if (toolState.brushType) toolStore.setBrushType(toolState.brushType)
    if (toolState.opacity) toolStore.setOpacity(toolState.opacity)
  }

  const handleMouseDown = async (e: MouseEvent, stage: KonvaStage): Promise<void> => {
    if (e.button !== 0) return

    if (cursorManager.isAnyInputActive()) {
      return
    }

    if (modeManager.isPanMode.value) {
      startPanning(e, stage)
    } else if (modeManager.isPositionMode.value) {
      return
    } else if (modeManager.isTextMode.value) {
      await handleTextPlacement(e, stage)
    } else {
      startDrawing(e, stage)
    }
  }

  const handleMouseMove = (e: MouseEvent, stage: KonvaStage): void => {
    if (cursorManager.isAnyInputActive()) {
      return
    }

    if (canvasStore.isPanning) {
      handlePanning(e, stage)
    } else if (modeManager.isPositionMode.value) {
      return
    } else if (canvasStore.isDrawing) {
      const pos = getCanvasPosition(e, stage)
      if (pos) {
        continueDrawing(pos, stage)
      }
    }
  }

  const handleMouseUp = (_e: MouseEvent, stage: KonvaStage): void => {
    if (cursorManager.isAnyInputActive()) {
      return
    }

    if (canvasStore.isPanning) {
      endPanning(stage)
    } else if (modeManager.isPositionMode.value) {
      return
    } else if (modeManager.isTextMode.value) {
      return
    } else if (canvasStore.isDrawing) {
      endDrawing(stage)
    }
  }
  const handleTouchStart = (e: TouchEvent, stage: KonvaStage): void => {
    e.preventDefault()

    if (cursorManager.isAnyInputActive()) {
      return
    }

    if (modeManager.isPanMode.value) {
      startPanning(e, stage)
    } else if (modeManager.isPositionMode.value) {
      return
    } else if (modeManager.isTextMode.value) {
      handleTextPlacement(e, stage)
    } else {
      startDrawing(e, stage)
    }
  }

  const handleTouchMove = (e: TouchEvent, stage: KonvaStage): void => {
    e.preventDefault()

    if (cursorManager.isAnyInputActive()) {
      return
    }

    if (canvasStore.isPanning) {
      handlePanning(e, stage)
    } else if (modeManager.isPositionMode.value) {
      return
    } else if (canvasStore.isDrawing) {
      const pos = getCanvasPosition(e, stage)
      if (pos) {
        continueDrawing(pos, stage)
      }
    }
  }

  const handleTouchEnd = (e: TouchEvent, stage: KonvaStage): void => {
    e.preventDefault()

    if (cursorManager.isAnyInputActive()) {
      return
    }

    if (canvasStore.isPanning) {
      endPanning(stage)
    } else if (modeManager.isPositionMode.value) {
      return
    } else if (modeManager.isTextMode.value) {
      return
    } else if (canvasStore.isDrawing) {
      endDrawing(stage)
    }
  }
  const handleKeyDown = (e: KeyboardEvent): void => {
    canvasStore.setKeyPressed(e.key.toLowerCase(), true)

    if (cursorManager.isAnyInputActive()) {
      return
    }

    if (e.key.toLowerCase() === 'p') {
      e.preventDefault()
      handleResetView()
    }

    if (e.key.toLowerCase() === 'm') {
      e.preventDefault()
      handleTogglePanMode()
    }

    handleBrushSizeShortcut(e)
  }

  const handleKeyUp = (e: KeyboardEvent): void => {
    canvasStore.setKeyPressed(e.key.toLowerCase(), false)
  }

  const handleWheelThrottled = useThrottleFn((e: WheelEvent, stage: KonvaStage) => {
    e.preventDefault()

    TaperingEngine.onZoomActivity()

    const center = getMousePositionRelativeToCanvas(e, stage)
    const zoomFactor = calculateZoomFactor(e.deltaY)

    canvasStore.zoom(zoomFactor, center, {
      forceImmediate: false,
      enableQualityScaling: true,
      enableThrottling: true
    })
  }, 16)

  const handleWheel = (e: WheelEvent, stage: KonvaStage): void => {
    handleWheelThrottled(e, stage)
  }
  const handleTextPlacement = async (e: MouseEvent | TouchEvent, stage: KonvaStage): Promise<void> => {
    const pos = getCanvasPosition(e, stage)
    if (!pos) return

    try {
      const { useTextStore } = await import('@/stores/qs-store-text')
      const textStore = useTextStore()

      if (textStore.isEditingText) {
        return
      }
      
      if (textStore.currentTextPreset) {
        const fontSize = textStore.currentTextPreset.fontSize
        const baselineOffset = fontSize * 0.2
        
        const textObject: TextObject = {
          id: `text-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          text: 'Text',
          fontSize: textStore.currentTextPreset.fontSize,
          fontFamily: textStore.currentTextPreset.fontFamily,
          fontWeight: textStore.currentTextPreset.fontWeight,
          fontStyle: textStore.currentTextPreset.fontStyle,
          fill: textStore.currentTextPreset.color,
          isTextObject: true,
          layerId: layerStore.currentLayerId || 'default',
          x: pos.x,
          y: pos.y - baselineOffset,
          timestamp: Date.now()
        }

        textStore.addTextObject(textObject)

        createKonvaTextObject(stage, textObject)

        const { useHistory } = await import('@/composables/qs-useHistory')
        const history = useHistory()
        history.addTextEntry(`Added text: "${textObject.text}"`)

        const konvaText = stage.findOne(`#${textObject.id}`)
        if (konvaText) {
          await startTextEditing(konvaText as Konva.Text, stage)
        }
      }
    } catch (error) {
      console.error('Failed to place text:', error)
    }
  }

  const createKonvaTextObject = (stage: KonvaStage, textObject: TextObject): void => {
    const currentLayer = stage.findOne(`#layer-${textObject.layerId}`)
    if (!currentLayer) return

    const konvaText = new Konva.Text({
      id: textObject.id,
      text: textObject.text,
      x: textObject.x,
      y: textObject.y,
      fontSize: textObject.fontSize,
      fontFamily: textObject.fontFamily,
      fontStyle: textObject.fontStyle,
      fill: textObject.fill,
      listening: true,
      isTextObject: true,
      name: 'text-object'
    })

    konvaText.on('click', async () => {
      try {
        const { useModeManager } = await import('@/composables/qs-useModeManager')
        const modeManager = useModeManager()
        
        if (modeManager.activeMode.value !== 'delete') {
          startTextEditing(konvaText, stage)
        }
      } catch (error) {
        console.error('Failed to check mode manager:', error)
        startTextEditing(konvaText, stage)
      }
    })

    currentLayer.add(konvaText)
    stage.batchDraw()
  }
  const restoreTextObjects = async (stage: KonvaStage): Promise<void> => {
    try {
      const { useTextStore } = await import('@/stores/qs-store-text')
      const textStore = useTextStore()

      const textObjects = textStore.textObjects
      
      if (textObjects && textObjects.length > 0) {
        console.log(`🔤 Restoring ${textObjects.length} text objects to canvas`)

        textObjects.forEach(textObject => {
          createKonvaTextObject(stage, textObject)
        })
        
        console.log('✅ Text objects restored successfully')
      }
    } catch (error) {
      console.error('❌ Failed to restore text objects:', error)
    }
  }

  const startTextEditing = async (konvaText: Konva.Text, stage: KonvaStage): Promise<void> => {
    try {
      const { useTextStore } = await import('@/stores/qs-store-text')
      const textStore = useTextStore()

      textStore.setEditingText(konvaText.id())

      const textNode = konvaText
      const stageBox = stage.container().getBoundingClientRect()

      const fontSize = textNode.fontSize()
      const absPos = textNode.getAbsolutePosition()
      const scaleX = stage.scaleX?.() ?? (stage.scaleX || 1)
      const scaleY = stage.scaleY?.() ?? (stage.scaleY || 1)
      const stagePos = stage.position ? stage.position() : { x: stage.x?.() ?? 0, y: stage.y?.() ?? 0 }

      const BASELINE_COEF = 0.15
      const baselineOffsetPx = BASELINE_COEF * fontSize * scaleY

      const areaPosition = {
        x: stageBox.left + stagePos.x + absPos.x * scaleX,
        y: stageBox.top + stagePos.y + absPos.y * scaleY - baselineOffsetPx,
        fontSize
      }

      const measureSpan = document.createElement('span')
      measureSpan.style.position = 'absolute'
      measureSpan.style.visibility = 'hidden'
      measureSpan.style.whiteSpace = 'nowrap'
      measureSpan.style.fontSize = fontSize + 'px'
      measureSpan.style.fontFamily = textNode.fontFamily()
      measureSpan.style.fontStyle = textNode.fontStyle()
      measureSpan.style.top = '-1000px'
      document.body.appendChild(measureSpan)

      const getTextWidth = (text: string) => {
        measureSpan.textContent = text || 'W'
        return Math.max(measureSpan.offsetWidth + 4, fontSize)
      }

      const textInput = document.createElement('input')
      textInput.value = textNode.text()

      textInput.style.position = 'absolute'
      textInput.style.top = areaPosition.y + 'px'
      textInput.style.left = areaPosition.x + 'px'
      textInput.style.width = getTextWidth(textInput.value) + 'px'
      textInput.style.height = (fontSize * 1.2) + 'px'

      textInput.style.fontSize = fontSize + 'px'
      textInput.style.fontFamily = textNode.fontFamily()
      textInput.style.fontStyle = textNode.fontStyle()

      const fontWeight = (textNode.attrs?.fontWeight ?? 'normal') as string
      textInput.style.fontWeight = fontWeight || 'normal'
      const fillColor = textNode.fill()
      textInput.style.color = typeof fillColor === 'string' ? fillColor : '#000000'

      textInput.style.border = 'none'
      textInput.style.outline = 'none'
      textInput.style.padding = '0'
      textInput.style.margin = '0'
      textInput.style.background = 'transparent'
      textInput.style.lineHeight = '1'
      textInput.style.letterSpacing = 'normal'
      textInput.style.wordSpacing = 'normal'
      textInput.style.textAlign = 'left'
      textInput.style.textDecoration = 'none'
      textInput.style.textTransform = 'none'
      textInput.style.textIndent = '0'
      textInput.style.boxSizing = 'border-box'
      textInput.style.borderRadius = '0'
      textInput.style.verticalAlign = 'baseline'
      ;(textInput.style as unknown as Record<string, string>).appearance = 'none'
      ;(textInput.style as unknown as Record<string, string>)['-webkit-appearance'] = 'none'
      ;(textInput.style as unknown as Record<string, string>)['-moz-appearance'] = 'none'
      textInput.style.zIndex = '1000'

      textNode.visible(false)
      stage.batchDraw()

      document.body.appendChild(textInput)

      const desiredTop = stageBox.top + stagePos.y + absPos.y * scaleY - baselineOffsetPx
      const actualTop = textInput.getBoundingClientRect().top
      const deltaTop = desiredTop - actualTop
      if (Math.abs(deltaTop) > 0.5) {
        const currentTop = parseFloat(textInput.style.top || '0')
        textInput.style.top = (currentTop + deltaTop) + 'px'
      }

      const originalInputPosition = {
        x: parseFloat(textInput.style.left || '0'),
        y: parseFloat(textInput.style.top || '0')
      }

      textInput.focus()
      textInput.select()

      const handleInput = () => {
        const newWidth = getTextWidth(textInput.value)
        textInput.style.width = newWidth + 'px'
      }
      
      textInput.addEventListener('input', handleInput)

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          confirmText()
        } else if (e.key === 'Escape') {
          e.preventDefault()
          cancelText()
        }
      }
      
      textInput.addEventListener('keydown', handleKeyDown)

      const confirmText = async () => {
        const newText = textInput.value.trim()
        const originalText = konvaText.text()
        
        if (newText) {
          textNode.text(newText)

          const stageBox = stage.container().getBoundingClientRect()
          const scaleX = stage.scaleX?.() ?? (stage.scaleX || 1)
          const scaleY = stage.scaleY?.() ?? (stage.scaleY || 1)
          const stagePos = stage.position ? stage.position() : { x: stage.x?.() ?? 0, y: stage.y?.() ?? 0 }

          const canvasX = (originalInputPosition.x - stageBox.left - stagePos.x) / scaleX
          const canvasY = (originalInputPosition.y - stageBox.top - stagePos.y + baselineOffsetPx) / scaleY

          textNode.position({ x: canvasX, y: canvasY })

          textStore.updateTextObject(konvaText.id(), { text: newText, x: canvasX, y: canvasY })

          if (originalText !== newText) {
            const { useHistory } = await import('@/composables/qs-useHistory')
            const history = useHistory()
            history.addTextEntry(`Edited text: "${originalText}" → "${newText}"`)
          }
        } else {
          textStore.removeTextObject(konvaText.id())
          konvaText.destroy()

          const { useHistory } = await import('@/composables/qs-useHistory')
          const history = useHistory()
          history.addTextEntry(`Deleted text: "${originalText}"`)
        }

        cleanup()

        setTimeout(() => {
          if (modeManager.activeMode.value !== 'drawing') {
            modeManager.switchToDrawingMode()
          }
          stage.batchDraw()
        }, 100)
      }

      const cancelText = () => {
        cleanup()

        setTimeout(() => {
          if (modeManager.activeMode.value !== 'drawing') {
            modeManager.switchToDrawingMode()
          }
          stage.batchDraw()
        }, 100)
      }

      const cleanup = () => {
        if (textInput) {
          textInput.removeEventListener('input', handleInput)
          textInput.removeEventListener('keydown', handleKeyDown)
        }
        if (document.body.contains(textInput)) {
          document.body.removeChild(textInput)
        }
        if (document.body.contains(measureSpan)) {
          document.body.removeChild(measureSpan)
        }
        document.removeEventListener('click', handleClickOutside)
        stage.off('mousedown', handleCanvasMouseDown)
        textStore.setEditingText(null)

        if (textNode && textNode.getParent()) {
          textNode.visible(true)
          stage.batchDraw()
        }
      }

      let isConfirming = false

      let isJustPlaced = true
      const placementGracePeriod = 300

      setTimeout(() => {
        isJustPlaced = false
      }, placementGracePeriod)

      const handleClickOutside = (e: MouseEvent) => {
        if (isConfirming || isJustPlaced) return
        
        if (!textInput.contains(e.target as Node)) {
          const target = e.target as Element
          
          const isFontSelector = target.closest('.font-selector')
          if (isFontSelector) {
            return
          }
          
          isConfirming = true
          confirmText()
        }
      }

      const handleCanvasMouseDown = (e: Konva.KonvaEventObject<MouseEvent>) => {
        if (isConfirming || isJustPlaced) return
        
        const originalEvent = e.evt || e
        if (originalEvent && originalEvent.target) {
          const target = originalEvent.target as Element
          const isCanvasClick = target.closest('.konvajs-content') || target.closest('#canvas-container')
          
          if (isCanvasClick && !textInput.contains(target as Node)) {
            isConfirming = true
            confirmText()
          }
        }
      }

      setTimeout(() => {
        document.addEventListener('click', handleClickOutside)

        stage.on('mousedown', handleCanvasMouseDown)
      }, placementGracePeriod + 50)
      
    } catch (error) {
      console.error('Failed to start text editing:', error)
    }
  }
  const startPanning = (e: MouseEvent | TouchEvent, stage: KonvaStage): void => {
    canvasStore.setPanning(true)
    const pos = getCanvasPosition(e, stage)
    if (pos) {
      canvasStore.setLastPanPosition(pos)
    }
  }

  const endPanning = (stage: KonvaStage): void => {
    if (panningAnimationFrame) {
      cancelAnimationFrame(panningAnimationFrame)
      panningAnimationFrame = null
    }

    canvasStore.setPanning(false)
    canvasStore.setLastPanPosition(null)
    stage.batchDraw()
  }

  const handlePanning = (e: MouseEvent | TouchEvent, stage: KonvaStage): void => {
    const pos = getCanvasPosition(e, stage)
    if (!pos || !canvasStore.lastPanPosition) return

    const deltaX = pos.x - canvasStore.lastPanPosition.x
    const deltaY = pos.y - canvasStore.lastPanPosition.y

    const newPosition = {
      x: canvasStore.position.x + deltaX,
      y: canvasStore.position.y + deltaY
    }

    if (panningAnimationFrame) {
      cancelAnimationFrame(panningAnimationFrame)
    }

    panningAnimationFrame = requestAnimationFrame(() => {
      stage.position(newPosition)
      canvasStore.setPosition(newPosition)
      panningAnimationFrame = null
    })

    canvasStore.setLastPanPosition(pos)
  }

  const handleResetView = (): void => {
    const panelStore = usePanelStore()
    panelStore.resetPanelPositions()
    canvasStore.centerArtboard()
  }

  const handleTogglePanMode = (): void => {
    if (modeManager.isPanMode.value) {
      modeManager.switchToDrawingMode()
    } else {
      modeManager.activatePanMode()
    }
  }

  const handleBrushSizeShortcut = (e: KeyboardEvent): void => {
    const key = e.key
    if (!['1', '2', '3', '4', '5'].includes(key)) return

    if (cursorManager.isAnyInputActive()) {
      return
    }

    e.preventDefault()
    const brushIndex = parseInt(key) - 1

    window.dispatchEvent(new CustomEvent('select-brush-by-index', {
      detail: { index: brushIndex }
    }))
  }

  const getMousePositionRelativeToCanvas = (e: WheelEvent, stage: KonvaStage) => {
    const container = stage.container()
    const rect = container.getBoundingClientRect()

    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    }
  }

  const calculateZoomFactor = (deltaY: number): number => {
    const zoomStep = 0.05
    return deltaY > 0 ? (1 - zoomStep) : (1 + zoomStep)
  }

  let eventListenerStops: Array<() => void> = []

  const setupEventListeners = (): void => {
    cleanupEventListeners()
    
    const stop1 = useEventListener(window, 'force-canvas-reinit', handleForceCanvasReinit)
    eventListenerStops.push(stop1)
    const stop2 = useEventListener(document, 'visibilitychange', handleVisibilityChange)
    eventListenerStops.push(stop2)
    const stop3 = useEventListener(window, 'layers-changed', handleLayerChange)
    eventListenerStops.push(stop3)
    const stop4 = useEventListener(window, 'layer-selected', handleLayerSelected)
    eventListenerStops.push(stop4)
    const stop5 = useEventListener(window, 'tool-state-refresh', handleToolStateRefresh)
    eventListenerStops.push(stop5)
  }

  const cleanupEventListeners = (): void => {
    eventListenerStops.forEach(stop => stop())
    eventListenerStops = []
  }

  return {
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleKeyDown,
    handleKeyUp,
    handleWheel,
    setupEventListeners,
    cleanupEventListeners,
    restoreTextObjects
  }
}